export interface OrganizationUser {
  id: number;
  name: string;
  status: string;
  is_admin: boolean;
  is_support: boolean;
  avatar_url: string;
}

export interface ExportRequest {
  id: number;
  status: ExportStatus;
  resource_type: string;
  created_at: string;
  expired_at: string;
  organization_user: OrganizationUser;
  resource_type_text: string;
  file_url?: string;
  download_url?: string;
  encoding?: string;
}

export interface ExportRequestsResponse {
  total_count: number;
  total_pages: number;
  current_page: number;
  next_page: number | null;
  prev_page: number | null;
  per_page: number;
  is_first_page: boolean;
  is_last_page: boolean;
  records: ExportRequest[];
}

// ExportableAttributes関連の型定義
export interface ExportableAttribute {
  column: string;
  label: string;
}

export interface CustomFieldOption {
  id: number;
  name: string;
  value: string;
  order_number: number;
}

export interface CustomCategory {
  id: number;
  name: string;
  color: string;
}

export interface CustomField {
  id: number;
  name: string;
  field_type: string;
  unit_name: string;
  order_number: number;
  is_required: boolean;
  alias_id: string;
  custom_field_options: CustomFieldOption[];
  custom_category: CustomCategory;
}

export interface ExportableAttributes {
  columns: ExportableAttribute[];
  custom_fields: CustomField[];
}

export type ExportStatus = 'failed' | 'running' | 'ready' | 'done';
export type ResourceType =
  | 'job_post'
  | 'candidate_profile'
  | 'job_change_process'
  | 'company'
  | 'candidate_interview_log'
  | 'job_post_endpoint'
  | 'job_change_process_log';
export type EncodingType = 'utf8' | 'utf8_bom' | 'shift_jis';

// データ種別ごとの期間指定対象カラム定義
export const DATE_COLUMN_OPTIONS: Record<ResourceType, { value: string; label: string }[]> = {
  job_post: [
    { value: 'created_at', label: '作成日' },
    { value: 'updated_at', label: '更新日' },
  ],
  candidate_profile: [
    { value: 'created_at', label: '登録日' },
    { value: 'updated_at', label: '更新日' },
  ],
  job_change_process: [
    { value: 'created_at', label: '作成日' },
    { value: 'last_acted_at', label: '最終アクション日' },
    { value: 'offered_at', label: '内定日' },
    { value: 'closed_at', label: '決定日' },
  ],
  company: [
    { value: 'created_at', label: '作成日' },
    { value: 'updated_at', label: '更新日' },
  ],
  candidate_interview_log: [
    { value: 'interviewed_at', label: '面談日' },
    { value: 'created_at', label: '作成日' },
  ],
  job_post_endpoint: [
    { value: 'created_at', label: '作成日' },
    { value: 'applied_at', label: '応募日' },
    { value: 'declined_at', label: '辞退日' },
    { value: 'matched_at', label: 'マッチ日' },
  ],
  job_change_process_log: [
    { value: 'interviewed_at', label: '選考日程' },
    { value: 'result_inserted_at', label: '結果確定日' },
    { value: 'created_at', label: '選考ログ作成日時' },
    { value: 'updated_at', label: '更新日' },
  ],
};

export const JOB_CHANGE_PROCESS_LOG_STEP_OPTIONS = [
  { value: 'document_screening', label: '書類選考' },
  { value: '1st_interview', label: '1次選考' },
  { value: '2nd_interview', label: '2次選考' },
  { value: '3rd_interview', label: '3次選考' },
  { value: '4th_interview', label: '4次選考以上' },
  { value: 'final_meeting', label: '最終選考' },
  { value: 'offer_meeting', label: 'オファー面談' },
  { value: 'offered', label: '内定' },
  { value: 'closed', label: '決定' },
] as const;

// POSTリクエスト用の型定義
export interface CreateExportRequestParams {
  resource_type: ResourceType;
  column: string[];
  custom_field_ids: number[];
  encoding?: EncodingType;
  date_from?: string | null;
  date_to?: string | null;
  date_column?: string;
  steps?: string[];
}
